import { gql, useMutation, useQuery } from '@apollo/client'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GetCurrentUserAutoPlaySettingQuery } from '~/__generated__/graphql'
import { useLogger } from '../../useLogger'
import { useVideoScale } from '../../useVideoScale'
import { PlayerButton } from '../PlayerButton'

type Props = {}

const GET_CURRENT_USER_AUTO_PLAY_QUERY = gql`
  query getCurrentUserAutoPlaySetting {
    currentUser {
      id
      autoplayVideos
    }
  }
`

const UPDATE_CURRENT_USER_AUTO_PLAY_MUTATION = gql`
  mutation updateCurrentUserAutoPlaySetting($autoplay: Boolean) {
    updateUser(input: { autoplayVideos: $autoplay }) {
      user {
        __typename
        id
        autoplayVideos
      }
      errors
    }
  }
`

export const AutoPlay = ({}: Props) => {
  const { data } = useQuery<GetCurrentUserAutoPlaySettingQuery>(GET_CURRENT_USER_AUTO_PLAY_QUERY)
  const autoPlay = useMemo(() => data?.currentUser?.autoplayVideos ?? undefined, [data])
  const [updateAutoPlaySetting] = useMutation(UPDATE_CURRENT_USER_AUTO_PLAY_MUTATION)

  const { videoScale } = useVideoScale()
  const { t } = useTranslation()
  const { clickAutoPlayButton } = useLogger()

  const handleChangeAutoPlay = useCallback(async () => {
    clickAutoPlayButton(!autoPlay ? 'on' : 'off')
    await updateAutoPlaySetting({
      variables: { autoplay: !autoPlay },
      optimisticResponse: {
        updateUser: {
          __typename: 'UpdateUserMutationPayload',
          user: {
            __typename: 'User',
            id: data?.currentUser?.id || '',
            autoplayVideos: !autoPlay,
          },
          errors: [],
        },
      },
    })
  }, [updateAutoPlaySetting, autoPlay, clickAutoPlayButton, data])

  return (
    <div style={{ position: 'relative' }}>
      <PlayerButton onClick={handleChangeAutoPlay}>
        <div
          style={{
            position: 'relative',
          }}
        >
          <Image
            src='/player/autoplay_bar.png'
            alt='Autoplay'
            width={videoScale * 78}
            height={videoScale * 26}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: autoPlay ? '100%' : '0%',
              transform: `translate(${autoPlay ? '-100%' : '0%'}, -50%)`,
              width: `${videoScale * 52}px`,
              height: `${videoScale * 52}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: autoPlay ? 1 : 0,
              transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
            }}
          >
            <Image
              src='/player/autoplay_on.png'
              alt='Autoplay On'
              width={videoScale * 52}
              height={videoScale * 52}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: autoPlay ? '100%' : '0%',
              transform: `translate(${autoPlay ? '-100%' : '0%'}, -50%)`,
              width: `${videoScale * 52}px`,
              height: `${videoScale * 52}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: autoPlay ? 0 : 1,
              transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
            }}
          >
            <Image
              src='/player/autoplay_off.png'
              alt='Autoplay Off'
              width={videoScale * 52}
              height={videoScale * 52}
            />
          </div>
        </div>
        {videoScale > 0.4 && (
          <span
            style={{
              fontSize: `${videoScale * 24}px`,
              fontWeight: 'bold',
              marginLeft: '8px',
            }}
          >
            {autoPlay ? t('Autoplay On') : t('Autoplay Off')}
          </span>
        )}
      </PlayerButton>
    </div>
  )
}
